import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { OnboardingService } from "@gtmhub/onboarding";
import { IPlanningSession, PlanningSessionsActions, ISessionsStoreState } from "@gtmhub/sessions";
import { INgRedux } from "@gtmhub/state-management";
import { StateService, StateParams, TransitionService } from "@uirouter/angularjs";
import { PermissionsService } from "@gtmhub/users";
import { IRootScopeService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { MISSING_SESSION_ID_STATE_PARAM } from "./all";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { DeleteSessionConfirmationService } from "@gtmhub/sessions/services/delete-session-confirmation-service";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";

export interface IGoalsRootScopeService extends IRootScopeService {
  noNavigation: boolean;
}

export interface IGoalsScope extends IModalScope, IScopeFromRedux, IExtendedRootScopeService {
  currentState: string;
  shouldSeeFirstOkrGuide: boolean;
  allowedActions: string[];
  isFavorite(): boolean;
  openCreateDialog(): void;
  confirmDeleteSession(): void;
  confirmDeleteDemoSession(): void;
  deletePlanningSession(sessionId: string): void;
  addToFavorites(): void;
  removeFromFavorites(): void;
}

interface IScopeFromRedux {
  planningSession: IPlanningSession;
  isDeleting: boolean;
  isDeleted: boolean;
  deleteSessionError: IUIError;
}

export class GoalsCtrl {
  public static $inject = [
    "$rootScope",
    "$transitions",
    "$scope",
    "$state",
    "$stateParams",
    "PlanningSessionsActions",
    "$ngRedux",
    "OnboardingService",
    "UIErrorHandlingService",
    "PermissionsService",
    "DeleteSessionConfirmationService",
    "FavoritesManagerFactory",
  ];
  private favoritesManager: FavoritesManager;

  constructor(
    private $rootScope: IGoalsRootScopeService & IGtmhubRootScopeService,
    private $transitions: TransitionService,
    private $scope: IGoalsScope,
    private $state: StateService,
    private $stateParams: StateParams,
    private planningSessionsActions: PlanningSessionsActions,
    private $ngRedux: INgRedux,
    private onboardingService: OnboardingService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private permissionsService: PermissionsService,
    private deleteSessionConfirmationService: DeleteSessionConfirmationService,
    private favoritesManagerFactory: FavoritesManagerFactory
  ) {
    if ($rootScope.newExperienceEnabledForUser) {
      this.favoritesManager = this.favoritesManagerFactory(["session"]);
      this.favoritesManager.initFavorites();
    }

    this.$rootScope.noNavigation = false;

    this.$scope.openCreateDialog = this.openCreateDialog;
    this.$scope.confirmDeleteSession = this.confirmDeleteSession;
    this.$scope.confirmDeleteDemoSession = this.confirmDeleteDemoSession;
    this.$scope.deletePlanningSession = this.deletePlanningSession;
    this.$scope.addToFavorites = this.addToFavorites;
    this.$scope.removeFromFavorites = this.removeFromFavorites;

    this.$scope.currentState = this.$state.current.name;

    this.$scope.$on(
      "$destroy",
      this.$transitions.onSuccess({}, () => {
        this.$scope.currentState = this.$state.current.name;
      })
    );

    const removeCloseFirstOkrGuideListener = this.$rootScope.$on(
      "closeFirstOkrGuide",
      () => (this.$scope.shouldSeeFirstOkrGuide = this.onboardingService.shouldSeeFirstOkrGuide())
    );

    const unsubscribe = this.$ngRedux.connect(this.bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", () => {
      unsubscribe();
      removeCloseFirstOkrGuideListener();
    });

    this.init();
  }

  private init = () => {
    const planningSessionId = this.$stateParams.planningSessionId;
    this.$scope.isFavorite = () => this.favoritesManager?.entityIsFavorite({ id: planningSessionId });

    if (planningSessionId === MISSING_SESSION_ID_STATE_PARAM || !planningSessionId) {
      this.$scope.planningSession = null;
    } else {
      this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
      this.getUserPermissions(planningSessionId);
    }
    this.$scope.shouldSeeFirstOkrGuide = this.onboardingService.shouldSeeFirstOkrGuide();
  };

  private addToFavorites = () => {
    this.favoritesManager?.addToFavorites(this.$scope.planningSession as { id: string }, "session", this.getNavItem());
  };

  private removeFromFavorites = () => {
    this.favoritesManager?.removeFromFavorites(this.$scope.planningSession as { id: string }, this.getNavItem());
  };

  private getNavItem = (): INavItem => {
    return this.favoritesManager?.getFavoriteItem({ id: this.$scope.planningSession.id }) || navItemsFactory.buildSessionNavItem(this.$scope.planningSession);
  };

  private getUserPermissions(planningSessionId: string): void {
    this.permissionsService.getCurrentUserAllowedActionsPerItem(planningSessionId, "session").then(
      (actions) => {
        this.$scope.allowedActions = actions.currentUserActions;
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private bindStateToScope = (state: ISessionsStoreState): IScopeFromRedux => {
    const isPlanningSessionNonExistant = this.$stateParams.planningSessionId === MISSING_SESSION_ID_STATE_PARAM || !this.$stateParams.planningSessionId;
    const planningSession = isPlanningSessionNonExistant ? null : state.sessions.items.find((s) => s.id === this.$stateParams.planningSessionId);
    return {
      planningSession,
      isDeleted: state.sessions.isDeleted,
      isDeleting: state.sessions.isDeleting,
      deleteSessionError: state.sessions.deleteSessionError,
    };
  };

  private openCreateDialog = (): void => {
    switch (this.$state.current.name.split(".")[2]) {
      case "overview":
      case "alignmentSidebar":
        this.$state.go(`${this.$state.current.name}._goalForm.form`, { id: null });
        break;
      default:
        this.$state.go("gtmhub.goals.all._goalForm.form", { id: null });
    }
  };

  private confirmDeleteSession = () => {
    const currentSession = this.$scope.planningSession;
    this.deleteSessionConfirmationService.confirmDeletePlanningSession(currentSession, () => this.deletePlanningSession(currentSession.id));
  };

  private deletePlanningSession = (planningSessionId: string) => {
    this.$ngRedux.dispatch(this.planningSessionsActions.deletePlanningSession(planningSessionId));
    this.onSessionDelete();
  };

  private onSessionDelete = () => {
    this.$scope
      .watchUntilAndRejectOnError(
        () => !this.$scope.isDeleting && this.$scope.isDeleted,
        () => this.$scope.deleteSessionError
      )
      .then(
        () => this.$state.go("gtmhub.sessions"),
        () => this.uiErrorHandlingService.handleModal(this.$scope.deleteSessionError)
      );
  };

  private confirmDeleteDemoSession = () => {
    this.deleteSessionConfirmationService.confirmDeleteDemoSession();
  };
}
